import { useEffect, useState } from 'react'
import { DocxDownloadSurface, DocxFolderSource } from '@/analytics/enums/docx-export.enums'
import { useAnalytics, type TrackEventFn } from '@/analytics/hooks/useAnalytics'
import { trackDocxExportDownloaded, trackDocxExportsFolderOpened } from '@/analytics/utils/docx-export-analytics'
import { DocxExportNoticeCard } from '@/agent-v2/docx-export/components/DocxExportNoticeCard'
import { DocxExportNoticePhase, type DocxExportNotice as DocxExportNoticeData } from '@/agent-v2/docx-export/schemas'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { matterPaths } from '@/routing/paths'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch } from '@/store/store-hooks'
import { downloadFile } from '@/user_files/files/fetch/download-user-file'

// Most exports finish in 3 to 5 minutes; past this, the card stops quoting that time.
const kDocxExportTakingLongerAfterMs = 6 * 60 * 1000

/** An export's chat notice, linked to the matter's exports folder and downloading in place. */
export function DocxExportNotice({
  notice,
  matterId,
  startedAt,
  conversationId,
}: {
  notice: DocxExportNoticeData
  matterId: string | null
  startedAt?: string
  conversationId?: string
}) {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const [isDownloading, setIsDownloading] = useState(false)
  const isTakingLonger = useHasElapsed(notice.phase === DocxExportNoticePhase.STARTED ? startedAt : undefined, kDocxExportTakingLongerAfterMs)
  const folderHref = matterId && notice.folder_id ? matterPaths.userFiles.folder(matterId, notice.folder_id) : null

  const handleDownload = async () => {
    const fileId = notice.file_id
    if (!fileId) return
    setIsDownloading(true)
    try {
      await downloadFile({ fileId, storagePath: '', fileName: notice.file_name ?? undefined })
      trackDocxExportDownloaded(trackEvent, {
        userFileId: fileId,
        docxDownloadSurface: DocxDownloadSurface.Chat,
        exportId: notice.export_id,
      })
    } catch {
      dispatch(
        openGlobalToast({ type: GlobalToastType.ERROR, message: 'Could not download the Word document. Please try again.', durationMs: kToastDurationShortMs })
      )
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <DocxExportNoticeCard
      notice={notice}
      folderHref={folderHref}
      onOpenFolder={notice.file_id ? () => trackChatCardFolderOpen(trackEvent, notice, conversationId) : undefined}
      onDownload={() => void handleDownload()}
      isDownloading={isDownloading}
      isTakingLonger={isTakingLonger}
    />
  )
}

function trackChatCardFolderOpen(track: TrackEventFn, notice: DocxExportNoticeData, conversationId: string | undefined) {
  if (!notice.file_id) return
  trackDocxExportsFolderOpened(track, {
    docxFolderSource: DocxFolderSource.ChatCard,
    exportId: notice.export_id,
    userFileId: notice.file_id,
    ...(conversationId ? { conversationId } : {}),
  })
}

/** Whether `durationMs` has passed since `since`; one timer re-renders at the deadline, without polling. */
function useHasElapsed(since: string | undefined, durationMs: number) {
  const deadline = since ? Date.parse(since) + durationMs : null
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (deadline === null || now >= deadline) return
    const timer = setTimeout(() => setNow(Date.now()), deadline - now)
    return () => clearTimeout(timer)
  }, [deadline, now])
  return deadline !== null && now >= deadline
}
